
import { useCallback, startTransition } from 'react';
import { Email } from '../../types';
import { markEmailAsRead, markEmailAsUnread } from '@/services/api/email';
import { emailCacheManager } from './emailCacheManager';
import { useToast } from '@/hooks/use-toast';

interface UseEmailMarkOperationsProps {
  emails: Email[];
  setEmails: (emails: Email[]) => void;
}

export const useEmailMarkOperations = ({
  emails,
  setEmails,
}: UseEmailMarkOperationsProps) => {
  const { toast } = useToast();

  const markAsRead = useCallback(async (id: string) => {
    // Optimistically update UI immediately
    const email = emails.find(e => e.id === id);
    const folder = email?.folder || 'inbox';
    
    const updatedEmails = emails.map(email =>
      email.id === id ? { ...email, read: true } : email
    );
    startTransition(() => {
      setEmails(updatedEmails);
    });
    // Update caches optimistically too — a folder-cache hit (the list refires
    // its load effect when `emails` changes, and back-navigation reads the
    // cache) must not resurrect the stale unread state.
    emailCacheManager.updateEmailReadStatus(id, true);

    try {
      await markEmailAsRead(id, folder);
    } catch (error) {
      console.error('Failed to mark as read, reverting:', error);
      // Revert state and cache on error
      emailCacheManager.updateEmailReadStatus(id, false);
      const revertedEmails = emails.map(email =>
        email.id === id ? { ...email, read: false } : email
      );
      startTransition(() => {
        setEmails(revertedEmails);
      });
      throw error;
    }
  }, [emails, setEmails]);

  const markAsUnread = useCallback(async (id: string) => {
    // Optimistically update UI immediately
    const email = emails.find(e => e.id === id);
    const previousEmails = [...emails];
    
    const updatedEmails = emails.map(email =>
      email.id === id ? { ...email, read: false } : email
    );
    startTransition(() => {
      setEmails(updatedEmails);
    });
    // Optimistic cache update (see markAsRead above)
    emailCacheManager.updateEmailReadStatus(id, false);

    try {
      await markEmailAsUnread(id, email?.folder || 'inbox');
    } catch (error) {
      console.error('Failed to mark as unread, reverting:', error);
      // Revert state and cache on error
      emailCacheManager.updateEmailReadStatus(id, true);
      startTransition(() => {
        setEmails(previousEmails);
      });
      
      toast({
        title: "Error",
        description: "Failed to mark email as unread",
        variant: "destructive",
      });
      
      throw error;
    }
  }, [emails, setEmails, toast]);

  return {
    markAsRead,
    markAsUnread,
  };
};
